// Profile → "Where you're signed in" (DESIGN-security.md, idea 8). Everyone sees their OWN —
// never an IP address (Rob's answer B is for admins).
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Monitor, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { securityApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { activeAgo, todayFrom } from '../../lib/securityWords';
import Flag from './Flag';
import HistoryLines from './HistoryLines';
import SecurityCredit from './SecurityCredit';

const isPhone = (device) => /iPhone|Android|iPad/.test(device || '');

export default function WhereSignedIn() {
  const { replaceToken, impersonation } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    securityApi.mine().then((r) => setData(r.data)).catch(() => setData({ failed: true }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const signOut = async (s) => {
    setBusy(s.id);
    try {
      await securityApi.signOutMySession(s.id);
      toast.success(`Signed out on ${s.device}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not sign that device out');
    } finally {
      setBusy(null);
    }
  };

  const others = async () => {
    setBusy('others');
    try {
      const r = await securityApi.signOutMyOthers();
      if (r.data?.access_token) replaceToken(r.data.access_token);
      toast.success("You've been signed out everywhere else");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not sign out your other devices');
    } finally {
      setBusy(null);
    }
  };

  if (!data) {
    return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }
  if (data.failed) {
    return <p className="text-sm text-muted-foreground">Couldn't load where you're signed in.</p>;
  }
  const sessions = data.sessions || [];
  const firstLine = data.days[0] && data.days[0].lines[0];

  return (
    <div data-testid="where-signed-in">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h3 className="font-semibold">Where you're signed in</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Something you don't recognise? Sign out everywhere else, then change your password.</p>
        </div>
        {!impersonation && (
          <Button variant="outline" size="sm" className="text-red-600 border-red-200 flex-shrink-0"
                  disabled={busy === 'others'} onClick={others} data-testid="sign-out-others">
            {busy === 'others' && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Sign out everywhere else
          </Button>
        )}
      </div>
      <div className="mt-2">
        {sessions.map((s) => (
          <div key={s.id} className="flex items-center gap-3 py-2.5 border-t border-border first:border-t-0">
            <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
              {isPhone(s.device) ? <Smartphone className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium flex items-center gap-1.5">
                {s.device}
                {s.this_device && <span className="text-[11px] rounded-full px-2 py-0.5 bg-secondary text-muted-foreground">This device</span>}
                {s.support && <span className="text-[11px] rounded-full px-2 py-0.5 bg-purple-500/15 text-purple-700 dark:text-purple-300">HolidayHive support</span>}
              </p>
              <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1.5">
                {s.country && <><Flag country={s.country} /><span>·</span></>}
                <span>{activeAgo(s.last_active_at)}</span>
              </p>
            </div>
            {!s.this_device && (
              <Button variant="outline" size="sm" disabled={busy === s.id} onClick={() => signOut(s)}>Sign out</Button>
            )}
          </div>
        ))}
      </div>
      <p className="text-[11px] font-bold tracking-wide uppercase text-muted-foreground mt-5">Recent sign-ins</p>
      <HistoryLines days={data.days} today={data.today || todayFrom(firstLine ? firstLine.at : null)} />
      <SecurityCredit credit={data.credit} />
    </div>
  );
}
